import React, { useMemo, useState } from 'react';
import { useAutomationOutbox } from '../../hooks/useAutomations';
import { useToast } from '../../components/Toast';

const STATUS_LABELS = {
    queued: 'Queued',
    scheduled: 'Scheduled',
    sending: 'Sending',
    sent: 'Sent',
    failed: 'Failed',
    canceled: 'Canceled',
    needs_approval: 'Needs Approval',
};

const CHANNEL_LABELS = {
    whatsapp: 'WhatsApp',
    sms: 'SMS',
    email: 'Email',
    telegram: 'Telegram',
    instagram_dm: 'Instagram DM',
    facebook_messenger: 'Facebook Messenger',
    instagram_comment_reply: 'IG Comment Reply',
    facebook_comment_reply: 'FB Comment Reply',
    linkedin_message: 'LinkedIn',
    webhook: 'Webhook',
};

const AutomationOutbox = () => {
    const { messages, loading, approve, cancel, retry, markSent, refetch } = useAutomationOutbox();
    const [selectedId, setSelectedId] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const { showSuccess, showError } = useToast();

    const filtered = useMemo(() => {
        if (statusFilter === 'all') return messages;
        return messages.filter((message) => message.status === statusFilter);
    }, [messages, statusFilter]);

    const selected = useMemo(
        () => filtered.find((message) => message.messageId === selectedId) || null,
        [filtered, selectedId],
    );

    const handleAction = async (action, messageId) => {
        try {
            if (action === 'approve') await approve(messageId);
            if (action === 'cancel') await cancel(messageId);
            if (action === 'retry') await retry(messageId);
            if (action === 'mark-sent') await markSent(messageId);
            showSuccess('Outbox updated');
            await refetch();
        } catch (err) {
            showError(err.message || 'Failed to update outbox');
        }
    };

    return (
        <div className="crm-grid" style={{ display: 'grid', gridTemplateColumns: '360px 1fr', gap: '20px' }}>
            <div className="crm-card crm-card-hover">
                <div className="crm-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h2 className="crm-card-title">Outbox</h2>
                    <select
                        className="crm-select crm-select-sm"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        {['all', 'queued', 'needs_approval', 'scheduled', 'sending', 'sent', 'failed', 'canceled'].map((status) => (
                            <option key={status} value={status}>{status.replace('_', ' ')}</option>
                        ))}
                    </select>
                </div>
                <div className="crm-card-body">
                    {loading ? (
                        <div className="crm-loading"><div className="crm-spinner" /></div>
                    ) : filtered.length === 0 ? (
                        <div className="crm-empty-state">
                            <p>No automated messages yet. Enable Playbooks or Rules to generate messages.</p>
                        </div>
                    ) : (
                        <div className="crm-list">
                            {filtered.map((message) => (
                                <button
                                    key={message.messageId}
                                    type="button"
                                    className="crm-list-row"
                                    onClick={() => setSelectedId(message.messageId)}
                                    style={{
                                        width: '100%',
                                        textAlign: 'left',
                                        background: message.messageId === selectedId ? 'var(--crm-sidebar-hover)' : 'transparent',
                                        border: '1px solid transparent',
                                        padding: '12px',
                                        borderRadius: '12px',
                                        marginBottom: '8px',
                                        color: '#fff',
                                    }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div>
                                            <div style={{ fontWeight: 600 }}>{message.meta?.leadName || message.leadId || 'Unknown lead'}</div>
                                            <div style={{ fontSize: '12px', color: 'var(--crm-gray-500)' }}>
                                                {message.channel} • {message.meta?.templateName || message.templateId || 'Template'}
                                            </div>
                                        </div>
                                        <span className={`crm-status-pill ${message.status}`}>{STATUS_LABELS[message.status]}</span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="crm-card crm-card-hover">
                <div className="crm-card-header">
                    <h2 className="crm-card-title">Message Detail</h2>
                </div>
                <div className="crm-card-body">
                    {!selected ? (
                        <div className="crm-empty-state">
                            <p>Select a message to view details.</p>
                        </div>
                    ) : (
                        <div style={{ display: 'grid', gap: '16px' }}>
                            <div className="crm-outbox-detail">
                                <div>
                                    <div className="crm-muted">Lead</div>
                                    <div>{selected.meta?.leadName || selected.leadId || 'Unknown'}</div>
                                </div>
                                <div>
                                    <div className="crm-muted">Channel</div>
                                    <div>{selected.channel}</div>
                                </div>
                                <div>
                                    <div className="crm-muted">Template</div>
                                    <div>
                                        {selected.meta?.templateName || selected.templateId || '—'}
                                        {selected.meta?.templateVersion ? ` v${selected.meta.templateVersion}` : ''}
                                    </div>
                                </div>
                                <div>
                                    <div className="crm-muted">Automation</div>
                                    <div>{selected.createdByAutomationName || selected.createdByAutomationId || '—'}</div>
                                </div>
                                <div>
                                    <div className="crm-muted">Status</div>
                                    <div>{STATUS_LABELS[selected.status]}</div>
                                </div>
                                <div>
                                    <div className="crm-muted">Scheduled</div>
                                    <div>{selected.scheduledAt ? new Date(selected.scheduledAt).toLocaleString() : '—'}</div>
                                </div>
                            </div>

                            <div className="crm-card crm-card-subtle">
                                <div className="crm-card-header">
                                    <h3 className="crm-card-title">Rendered Message</h3>
                                </div>
                                <div className="crm-card-body">
                                    <p style={{ whiteSpace: 'pre-line' }}>{selected.renderedText}</p>
                                    {selected.sendResult && (
                                        <p className="crm-muted" style={{ marginTop: '12px' }}>
                                            Reason: {selected.sendResult}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {selected.manual?.enabled && !['sent', 'canceled'].includes(selected.status) && (
                                <div className="crm-card crm-card-subtle">
                                    <div className="crm-card-header">
                                        <h3 className="crm-card-title">Manual Send</h3>
                                    </div>
                                    <div className="crm-card-body">
                                        <p className="crm-muted">{selected.manual.instructions || 'Copy and send manually.'}</p>
                                        <div className="crm-form-actions">
                                            <button
                                                className="crm-btn crm-btn-secondary"
                                                onClick={() => navigator.clipboard.writeText(selected.renderedText || '')}
                                            >
                                                Copy message
                                            </button>
                                            {selected.manual.openUrl && (
                                                <button
                                                    className="crm-btn crm-btn-secondary"
                                                    onClick={() => window.open(selected.manual.openUrl, '_blank')}
                                                >
                                                    Open {CHANNEL_LABELS[selected.channel] || selected.channel}
                                                </button>
                                            )}
                                            <button
                                                className="crm-btn crm-btn-primary"
                                                onClick={() => handleAction('mark-sent', selected.messageId)}
                                            >
                                                Mark as Sent
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="crm-form-actions">
                                {selected.status === 'needs_approval' && (
                                    <button className="crm-btn crm-btn-primary" onClick={() => handleAction('approve', selected.messageId)}>
                                        Approve
                                    </button>
                                )}
                                {selected.status === 'failed' && (
                                    <button className="crm-btn crm-btn-secondary" onClick={() => handleAction('retry', selected.messageId)}>
                                        Retry
                                    </button>
                                )}
                                {['queued', 'needs_approval', 'scheduled', 'failed'].includes(selected.status) && (
                                    <button className="crm-btn crm-btn-secondary" onClick={() => handleAction('cancel', selected.messageId)}>
                                        Cancel
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AutomationOutbox;
